import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HiTrash, HiExclamationTriangle, HiLockClosed } from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { deleteAccount } from '../../services/settingsService';
import { toast } from '../../utils/toast';

export default function DeleteAccountSettings() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const CONFIRM_TEXT = 'DELETE MY ACCOUNT';

  const handleDelete = async () => {
    if (!password) {
      toast.error('Password is required');
      return;
    }

    if (confirmText !== CONFIRM_TEXT) {
      toast.error(`Please type "${CONFIRM_TEXT}" to confirm`);
      return;
    }

    try {
      setDeleting(true);
      await deleteAccount({
        uid: currentUser.uid,
        password,
        reason,
      });

      toast.success('Account deleted successfully');

      // Redirect to home page after deletion
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast.error(error.message || 'Failed to delete account');
      setDeleting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-red-200 bg-white shadow-lg ring-1 ring-red-100">
      {/* Header */}
      <div className="border-b border-red-200 bg-gradient-to-r from-red-50 to-orange-50 px-6 py-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-600 text-white shadow-lg shadow-red-500/30">
            <HiTrash className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-black">Delete Account</h2>
            <p className="text-sm text-red-700">Permanently remove your account and all data</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8">
        {/* Warning Box */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-lg border-2 border-red-300 bg-red-50 p-6"
        >
          <div className="flex gap-4">
            <HiExclamationTriangle className="h-8 w-8 flex-shrink-0 text-red-600" />
            <div>
              <h3 className="font-bold text-red-900">Warning: This action is irreversible!</h3>
              <ul className="mt-3 space-y-2 text-sm text-red-800">
                <li className="flex items-start gap-2">
                  <span className="font-bold">•</span>
                  <span>Your account will be permanently deleted from all collections</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">•</span>
                  <span>All your personal data, documents, and settings will be removed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">•</span>
                  <span>Your profile photo and uploads will be deleted from storage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">•</span>
                  <span>This action cannot be undone - there is no recovery option</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {!showConfirm ? (
            <motion.button
              key="show-confirm-btn"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowConfirm(true)}
              className="w-full rounded-lg border-2 border-red-600 bg-white px-6 py-3 text-sm font-semibold text-red-600 transition-all hover:bg-red-50"
            >
              I want to delete my account
            </motion.button>
          ) : (
            <motion.div
              key="delete-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Reason (Optional) */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  Reason for leaving (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                  placeholder="Help us improve by telling us why you're leaving..."
                  maxLength={500}
                />
                <p className="mt-1 text-xs text-gray-500">{reason.length}/500 characters</p>
              </div>

              {/* Password Confirmation */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  Confirm your password <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <HiLockClosed className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full rounded-lg border-2 border-gray-300 bg-white py-3 pl-10 pr-4 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-red-500 focus:outline-none focus:ring-4 focus:ring-red-500/10"
                    placeholder="Enter your password"
                    required
                  />
                </div>
              </div>

              {/* Confirmation Text */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  Type "{CONFIRM_TEXT}" to confirm <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-red-500 focus:outline-none focus:ring-4 focus:ring-red-500/10"
                  placeholder={CONFIRM_TEXT}
                  required
                />
                {confirmText && confirmText !== CONFIRM_TEXT && (
                  <p className="mt-2 text-xs text-red-600">
                    Text doesn't match. Please type exactly: {CONFIRM_TEXT}
                  </p>
                )}
              </div>

              {/* Final Confirmation Box */}
              <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="final-confirm"
                    className="h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                    required
                  />
                  <label htmlFor="final-confirm" className="text-sm font-semibold text-red-900">
                    I understand that this action is permanent and cannot be undone
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 border-t-2 border-red-200 pt-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowConfirm(false);
                    setPassword('');
                    setReason('');
                    setConfirmText('');
                  }}
                  disabled={deleting}
                  className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-black transition-all hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDelete}
                  disabled={deleting || !password || confirmText !== CONFIRM_TEXT}
                  className="flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-red-500/30 transition-all hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                      />
                      Deleting Account...
                    </>
                  ) : (
                    <>
                      <HiTrash className="h-4 w-4" />
                      Permanently Delete Account
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
